<template>
    <vx-table-row class="min-h-[72px]">
        <vx-table-row-item>
            <template v-slot:label> Coupon Code: </template>

            <vx-popover
                :show="copiedText == props.modelValue.code"
                :arrow="true"
                placement="top"
                class="cursor-pointer font-mono border-solid border border-slate-200 bg-slate-50 text-slate-800 text-xs py-1 px-2 rounded-[4px]"
                @click.prevent="copyText(props.modelValue.code, false)"
            >
                <button
                    type="button"
                    class="cursor-pointer block max-w-full text-left focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
                    data-test="coupon-code"
                >
                    <span class="block truncate">{{ props.modelValue.code }}</span>
                </button>
                <template v-slot:content> Copied </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-item class="xl:col-span-2!">
            <template v-slot:label> Allocated to: </template>

            <a
                v-if="props.modelValue.contact_id"
                :href="`/contacts/${props.modelValue.contact_id}/view`"
                class="flex gap-1 w-full no-underline!"
            >
                <strong
                    class="whitespace-nowrap text-slate-800 border-b border-solid border-slate-200 border-x-0 border-t-0"
                    >{{ formatPhoneNumber(props.modelValue.contact_phone) }}</strong
                >
                <span v-if="props.modelValue.contact_name" class="text-slate-500 truncate">{{
                    props.modelValue.contact_name
                }}</span>
            </a>
            <div
                v-else-if="!props.modelValue.contact_id && props.modelValue.allocated_at"
                class="flex items-center gap-2"
            >
                <font-awesome-icon widthAuto :icon="faGhost" class="text-slate-300"></font-awesome-icon>
                <span class="text-slate-400">Deleted Contact</span>
            </div>
            <template v-else>-</template>
        </vx-table-row-item>

        <vx-table-row-item class="xl:col-span-2!">
            <template v-slot:label> Allocated by: </template>

            <component
                :is="resourceLink ? 'a' : 'div'"
                v-if="props.modelValue.resource_id && props.modelValue.resource_name"
                :href="resourceLink"
                class="flex items-center gap-2 no-underline!"
            >
                <font-awesome-icon
                    widthAuto
                    :icon="resourceTypeIcon(props.modelValue.resource_type)"
                    class="text-slate-300"
                ></font-awesome-icon>
                <span class="text-slate-800 border-b border-solid border-slate-200 border-x-0 border-t-0 truncate">{{
                    props.modelValue.resource_name
                }}</span>
            </component>
            <template v-else> - </template>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Allocated at: </template>

            <vx-popover v-if="props.modelValue.allocated_at" hover :arrow="true" placement="top" :key="allocatedAtKey">
                {{ fromNow(props.modelValue.allocated_at) }}
                <template v-slot:content>
                    {{ shortDateTime(props.modelValue.allocated_at) }}
                </template>
            </vx-popover>
            <div v-else>-</div>
        </vx-table-row-item>
    </vx-table-row>
</template>

<script setup>
import { faGhost } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxPopover, VxTableRow, VxTableRowItem } from '@voxie/frontend-components';
import { useInterval } from '@vueuse/core';
import { computed } from 'vue';
import { formatPhoneNumber } from '~/components/filters';
import { useCopyText } from '~/composables/useCopyText';
import { resourceTypeIcon } from '~/utils/analytics';
import { fromNow, shortDateTime } from '~/utils/date';

const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
});

const allocatedAtKey = useInterval(60000);

const { copyText, copiedText } = useCopyText();

const resourceLink = computed(() => {
    if (props.modelValue.resource_type === 'automation') {
        return '/automation/event/' + props.modelValue.resource_id;
    }
    if (
        props.modelValue.resource_type === 'one_time_campaign' ||
        props.modelValue.resource_type === 'evergreen_campaign'
    ) {
        return '/messaging/campaigns/' + props.modelValue.resource_id;
    }
    return undefined;
});
</script>
